// src/pages/ClientSupport.js
import React, { useState, useEffect } from 'react';
import {
    Container,
    Typography,
    Box,
    Paper,
    Grid,
    Button,
    TextField,
    MenuItem,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Tab,
    Tabs,
    Chip,
    CircularProgress,
    Alert,
    Snackbar,
    Divider,
    Card,
    CardContent,
    useTheme,
    useMediaQuery
} from '@mui/material';
import {
    SupportAgent as SupportIcon,
    Add as AddIcon,
    Visibility as ViewIcon,
    CheckCircle as CheckIcon,
    QuestionAnswer as AnswerIcon,
    Close as CloseIcon,
    ChatBubble as ChatIcon,
    Book as BookIcon,
    Search as SearchIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import FiberLoader from '../components/common/FiberLoader';

// Componente de TabPanel
const TabPanel = (props) => {
    const { children, value, index, ...other } = props;
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`simple-tabpanel-${index}`}
            aria-labelledby={`simple-tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box className="p-3">
                    {children}
                </Box>
            )}
        </div>
    );
};

const ClientSupport = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [tickets, setTickets] = useState([]);
    const [knowledgeBase, setKnowledgeBase] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [tabValue, setTabValue] = useState(0);
    const [openNewTicketDialog, setOpenNewTicketDialog] = useState(false);
    const [openTicketDetailDialog, setOpenTicketDetailDialog] = useState(false);
    const [selectedTicket, setSelectedTicket] = useState(null);
    const [ticketLoading, setTicketLoading] = useState(false);
    const [ticketResponse, setTicketResponse] = useState('');
    const [newTicket, setNewTicket] = useState({
        subject: '',
        description: '',
        category: '',
        priority: 'medium'
    });
    const [searchQuery, setSearchQuery] = useState('');
    const [filteredKnowledge, setFilteredKnowledge] = useState([]);
    const [successMessage, setSuccessMessage] = useState('');

    useEffect(() => {
        fetchTickets();
        fetchKnowledgeBase();
    }, []);

    useEffect(() => {
        if (knowledgeBase.length > 0) {
            filterKnowledgeBase();
        }
    }, [searchQuery, knowledgeBase]);

    const fetchTickets = async () => {
        try {
            const token = localStorage.getItem('clientToken');
            const response = await fetch('/api/client/support/tickets', {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) {
                throw new Error('Error al obtener tickets');
            }

            const data = await response.json();
            setTickets(data);
        } catch (error) {
            console.error('Error fetching tickets:', error);
            setError(error.message || 'Error al cargar los tickets');
        } finally {
            setLoading(false);
        }
    };

    const fetchKnowledgeBase = async () => {
        try {
            const token = localStorage.getItem('clientToken');
            const response = await fetch('/api/client/support/knowledge', {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.ok) {
                const data = await response.json();
                setKnowledgeBase(data);
                setFilteredKnowledge(data);
            }
        } catch (error) {
            console.error('Error fetching knowledge base:', error);
        }
    };

    const filterKnowledgeBase = () => {
        if (!searchQuery) {
            setFilteredKnowledge(knowledgeBase);
            return;
        }

        const query = searchQuery.toLowerCase();
        const filtered = knowledgeBase.filter(
            article =>
                article.title.toLowerCase().includes(query) ||
                article.content.toLowerCase().includes(query) ||
                (article.tags && article.tags.some(tag => tag.toLowerCase().includes(query)))
        );

        setFilteredKnowledge(filtered);
    };

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
    };

    const handleOpenNewTicket = () => {
        setNewTicket({
            subject: '',
            description: '',
            category: '',
            priority: 'medium'
        });
        setOpenNewTicketDialog(true);
    };

    const handleCloseNewTicket = () => {
        setOpenNewTicketDialog(false);
    };

    const handleTicketChange = (e) => {
        const { name, value } = e.target;
        setNewTicket(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmitTicket = async () => {
        try {
            setTicketLoading(true);
            setError(null);
            const token = localStorage.getItem('clientToken');
            const response = await fetch('/api/client/support/tickets', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(newTicket)
            });

            if (!response.ok) {
                let errorMsg = 'Error al crear ticket';
                try {
                    const errorData = await response.json();
                    errorMsg = errorData.message || errorMsg;
                } catch (e) { /* ignorar */ }
                throw new Error(errorMsg);
            }

            // Cerrar el diálogo
            handleCloseNewTicket();
            setSuccessMessage('Ticket creado exitosamente');

            // Pequeña espera para asegurar consistencia de base de datos y recargar tickets
            setTimeout(async () => {
                await fetchTickets();
            }, 500);
        } catch (error) {
            console.error('Error creating ticket:', error);
            setError(error.message || 'Error al crear ticket');
        } finally {
            setTicketLoading(false);
        }
    };

    const handleViewTicket = async (ticketId) => {
        try {
            setTicketLoading(true);
            const token = localStorage.getItem('clientToken');
            const response = await fetch(`/api/client/support/tickets/${ticketId}`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) {
                throw new Error('Error al obtener detalles del ticket');
            }

            const data = await response.json();
            setSelectedTicket(data);
            setTicketResponse('');
            setOpenTicketDetailDialog(true);
        } catch (error) {
            console.error('Error fetching ticket details:', error);
            setError(error.message || 'Error al obtener detalles del ticket');
        } finally {
            setTicketLoading(false);
        }
    };

    const handleCloseTicketDetail = () => {
        setOpenTicketDetailDialog(false);
        setSelectedTicket(null);
    };

    const handleResponseChange = (e) => {
        setTicketResponse(e.target.value);
    };

    const handleSubmitResponse = async () => {
        if (!ticketResponse.trim()) return;

        try {
            setTicketLoading(true);
            const token = localStorage.getItem('clientToken');
            const response = await fetch(`/api/client/support/tickets/${selectedTicket.id}/responses`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ content: ticketResponse })
            });

            if (!response.ok) {
                throw new Error('Error al enviar respuesta');
            }

            // Recargar ticket y limpiar respuesta
            await handleViewTicket(selectedTicket.id);
            setTicketResponse('');
        } catch (error) {
            console.error('Error submitting response:', error);
            setError(error.message || 'Error al enviar respuesta');
        } finally {
            setTicketLoading(false);
        }
    };

    const handleCloseTicket = async () => {
        try {
            setTicketLoading(true);
            const token = localStorage.getItem('clientToken');
            const response = await fetch(`/api/client/support/tickets/${selectedTicket.id}/close`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) {
                throw new Error('Error al cerrar ticket');
            }

            // Cerrar diálogo y recargar tickets
            handleCloseTicketDetail();
            await fetchTickets();
            setSuccessMessage('Ticket cerrado exitosamente');
        } catch (error) {
            console.error('Error closing ticket:', error);
            setError(error.message || 'Error al cerrar ticket');
        } finally {
            setTicketLoading(false);
        }
    };

    const getStatusChip = (status) => {
        switch (status) {
            case 'open':
                return <Chip label="Abierto" color="primary" size="small" />;
            case 'closed':
                return <Chip label="Cerrado" color="default" size="small" />;
            case 'in_progress':
                return <Chip label="En Progreso" color="secondary" size="small" />;
            case 'client_response':
                return <Chip label="Esperando Respuesta" color="warning" size="small" />;
            default:
                return <Chip label={status} size="small" />;
        }
    };

    const getPriorityChip = (priority) => {
        switch (priority) {
            case 'high':
                return <Chip label="Alta" color="error" size="small" />;
            case 'medium':
                return <Chip label="Media" color="warning" size="small" />;
            case 'low':
                return <Chip label="Baja" color="success" size="small" />;
            default:
                return <Chip label={priority} size="small" />;
        }
    };

    const ticketCategories = [
        { value: 'technical', label: 'Soporte Técnico' },
        { value: 'billing', label: 'Facturación' },
        { value: 'connection', label: 'Problemas de Conexión' },
        { value: 'installation', label: 'Instalación' },
        { value: 'other', label: 'Otro' }
    ];

    const ticketPriorities = [
        { value: 'low', label: 'Baja' },
        { value: 'medium', label: 'Media' },
        { value: 'high', label: 'Alta' }
    ];

    if (loading) {
        return (
            <FiberLoader text="Conectando con centro de soporte de fibra óptica..." subtext="Cargando tickets de servicio y base de conocimientos..." fullPage />
        );
    }

    return (
        <Container maxWidth="lg">
            <Box className="mb-6">
                <Typography variant="h4" component="h1" gutterBottom>
                    <SupportIcon className="mr-2 align-top" />
                    Soporte Técnico
                </Typography>
                <Typography variant="body1" color="textSecondary">
                    Gestiona tus tickets de soporte y encuentra ayuda en nuestra base de conocimientos
                </Typography>
            </Box>

            <Box className="mb-6">
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', md: 'center' }, gap: 2, mb: 4 }}>
                    <Tabs value={tabValue} onChange={handleTabChange} variant={isMobile ? "fullWidth" : "standard"}>
                        <Tab label="Mis Tickets" icon={<ChatIcon />} iconPosition="start" />
                        <Tab label="Base de Conocimientos" icon={<BookIcon />} iconPosition="start" />
                    </Tabs>

                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<AddIcon />}
                        onClick={handleOpenNewTicket}
                        sx={{ borderRadius: 2 }}
                    >
                        Nuevo Ticket
                    </Button>
                </Box>

                <TabPanel value={tabValue} index={0}>
                    {error && (
                        <Alert severity="error" className="mb-4">
                            {error}
                        </Alert>
                    )}

                    {tickets.length === 0 ? (
                        <Paper className="p-4 text-center" sx={{ borderRadius: 2 }}>
                            <Typography variant="h6">No tienes tickets de soporte</Typography>
                            <Button
                                variant="outlined"
                                color="primary"
                                className="mt-4"
                                startIcon={<AddIcon />}
                                onClick={handleOpenNewTicket}
                                sx={{ borderRadius: 2 }}
                            >
                                Crear Ticket
                            </Button>
                        </Paper>
                    ) : isMobile ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            {tickets.map((ticket) => (
                                <Card key={ticket.id} elevation={1} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
                                    <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                                            <Box sx={{ maxWidth: '70%' }}>
                                                <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 0.2 }}>
                                                    Ticket #{ticket.id.slice(0, 8)}
                                                </Typography>
                                                <Typography variant="body1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                                                    {ticket.subject}
                                                </Typography>
                                            </Box>
                                            {getStatusChip(ticket.status)}
                                        </Box>

                                        <Grid container spacing={2} sx={{ mb: 2 }}>
                                            <Grid item xs={6}>
                                                <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 0.2 }}>
                                                    Categoría
                                                </Typography>
                                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                    {ticket.category ? (
                                                        ticketCategories.find(c => c.value === ticket.category)?.label || ticket.category
                                                    ) : 'General'}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={6}>
                                                <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 0.2 }}>
                                                    Prioridad
                                                </Typography>
                                                {getPriorityChip(ticket.priority)}
                                            </Grid>
                                            <Grid item xs={12}>
                                                <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 0.2 }}>
                                                    Fecha de Creación
                                                </Typography>
                                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                    {format(new Date(ticket.created_at), 'dd/MM/yyyy')}
                                                </Typography>
                                            </Grid>
                                        </Grid>

                                        <Box sx={{ borderTop: '1px solid', borderColor: 'divider', pt: 1.5, display: 'flex', justifyContent: 'flex-end' }}>
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                onClick={() => handleViewTicket(ticket.id)}
                                                startIcon={<ViewIcon sx={{ fontSize: '1rem !important' }} />}
                                                sx={{ borderRadius: 2 }}
                                            >
                                                Ver Detalles
                                            </Button>
                                        </Box>
                                    </CardContent>
                                </Card>
                            ))}
                        </Box>
                    ) : (
                        <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>ID</TableCell>
                                        <TableCell>Asunto</TableCell>
                                        <TableCell>Categoría</TableCell>
                                        <TableCell>Prioridad</TableCell>
                                        <TableCell>Estado</TableCell>
                                        <TableCell>Fecha</TableCell>
                                        <TableCell>Acciones</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {tickets.map((ticket) => (
                                        <TableRow key={ticket.id}>
                                            <TableCell>#{ticket.id.slice(0, 8)}</TableCell>
                                            <TableCell>{ticket.subject}</TableCell>
                                            <TableCell>{ticket.category || 'General'}</TableCell>
                                            <TableCell>{getPriorityChip(ticket.priority)}</TableCell>
                                            <TableCell>{getStatusChip(ticket.status)}</TableCell>
                                            <TableCell>{format(new Date(ticket.created_at), 'dd/MM/yyyy')}</TableCell>
                                            <TableCell>
                                                <IconButton
                                                    size="small"
                                                    color="primary"
                                                    onClick={() => handleViewTicket(ticket.id)}
                                                >
                                                    <ViewIcon />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </TabPanel>

                <TabPanel value={tabValue} index={1}>
                    <Box className="mb-4">
                        <TextField
                            fullWidth
                            placeholder="Buscar en la base de conocimientos..."
                            variant="outlined"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            InputProps={{
                                startAdornment: <SearchIcon className="mr-2 text-gray-500" />
                            }}
                        />
                    </Box>

                    {filteredKnowledge.length === 0 ? (
                        <Paper className="p-4 text-center">
                            <Typography variant="h6">No se encontraron artículos</Typography>
                        </Paper>
                    ) : (
                        <Grid container spacing={3}>
                            {filteredKnowledge.map((article) => (
                                <Grid item xs={12} md={6} key={article.id}>
                                    <Paper className="p-4 h-full">
                                        <Typography variant="h6" className="mb-2">{article.title}</Typography>
                                        <Typography variant="body2" color="textSecondary" className="mb-3">
                                            {article.content.substring(0, 150)}...
                                        </Typography>
                                        <Box className="flex flex-wrap gap-1 mb-3">
                                            {article.tags && article.tags.map((tag, index) => (
                                                <Chip key={index} label={tag} size="small" />
                                            ))}
                                        </Box>
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            onClick={() => window.open(`/client/support/knowledge/${article.id}`, '_blank')}
                                        >
                                            Leer más
                                        </Button>
                                    </Paper>
                                </Grid>
                            ))}
                        </Grid>
                    )}
                </TabPanel>
            </Box>

            {/* Diálogo para Nuevo Ticket */}
            <Dialog open={openNewTicketDialog} onClose={handleCloseNewTicket} maxWidth="md" fullWidth>
                <DialogTitle>Nuevo Ticket de Soporte</DialogTitle>
                <DialogContent>
                    <Box className="p-2 space-y-4">
                        <TextField
                            fullWidth
                            label="Asunto"
                            name="subject"
                            value={newTicket.subject}
                            onChange={handleTicketChange}
                            required
                        />

                        <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    select
                                    label="Categoría"
                                    name="category"
                                    value={newTicket.category}
                                    onChange={handleTicketChange}
                                >
                                    <MenuItem value="">Selecciona una categoría</MenuItem>
                                    {ticketCategories.map(category => (
                                        <MenuItem key={category.value} value={category.value}>
                                            {category.label}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    select
                                    label="Prioridad"
                                    name="priority"
                                    value={newTicket.priority}
                                    onChange={handleTicketChange}
                                >
                                    {ticketPriorities.map(priority => (
                                        <MenuItem key={priority.value} value={priority.value}>
                                            {priority.label}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                        </Grid>

                        <TextField
                            fullWidth
                            multiline
                            rows={6}
                            label="Descripción"
                            name="description"
                            value={newTicket.description}
                            onChange={handleTicketChange}
                            required
                            placeholder="Describe tu problema o consulta con el mayor detalle posible..."
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseNewTicket}>Cancelar</Button>
                    <Button
                        onClick={handleSubmitTicket}
                        variant="contained"
                        color="primary"
                        disabled={!newTicket.subject || !newTicket.description || ticketLoading}
                    >
                        {ticketLoading ? <CircularProgress size={24} /> : 'Enviar Ticket'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Diálogo para Ver Detalles del Ticket */}
            <Dialog open={openTicketDetailDialog} onClose={handleCloseTicketDetail} maxWidth="md" fullWidth>
                {selectedTicket && (
                    <>
                        <DialogTitle>
                            <Box className="flex justify-between items-center">
                                <Typography variant="h6">Ticket: {selectedTicket.subject}</Typography>
                                {getStatusChip(selectedTicket.status)}
                            </Box>
                        </DialogTitle>
                        <DialogContent>
                            <Box className="p-2">
                                <Grid container spacing={2} className="mb-4">
                                    <Grid item xs={12} md={3}>
                                        <Typography variant="body2" color="textSecondary">ID:</Typography>
                                        <Typography variant="body1">#{selectedTicket.id.slice(0, 8)}</Typography>
                                    </Grid>
                                    <Grid item xs={12} md={3}>
                                        <Typography variant="body2" color="textSecondary">Fecha:</Typography>
                                        <Typography variant="body1">
                                            {format(new Date(selectedTicket.created_at), 'dd/MM/yyyy HH:mm')}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} md={3}>
                                        <Typography variant="body2" color="textSecondary">Categoría:</Typography>
                                        <Typography variant="body1">{selectedTicket.category || 'General'}</Typography>
                                    </Grid>
                                    <Grid item xs={12} md={3}>
                                        <Typography variant="body2" color="textSecondary">Prioridad:</Typography>
                                        <Typography variant="body1">{getPriorityChip(selectedTicket.priority)}</Typography>
                                    </Grid>
                                </Grid>

                                <Box className="mb-4">
                                    <Typography variant="body2" color="textSecondary">Descripción:</Typography>
                                    <Paper variant="outlined" className="p-3 bg-gray-50 mt-1">
                                        <Typography variant="body1">{selectedTicket.description}</Typography>
                                    </Paper>
                                </Box>

                                <Divider className="mb-4" />

                                <Typography variant="h6" gutterBottom>Respuestas</Typography>

                                {selectedTicket.responses && selectedTicket.responses.length > 0 ? (
                                    <Box className="space-y-4">
                                        {selectedTicket.responses.map((response, index) => (
                                            <Paper
                                                key={index}
                                                variant="outlined"
                                                className={`p-3 ${response.response_type === 'staff' ? 'bg-blue-50' : 'bg-green-50'}`}
                                            >
                                                <Box className="flex justify-between items-center mb-2">
                                                    <Typography variant="subtitle2">
                                                        {response.response_type === 'staff' ? 'Soporte' : 'Tú'}
                                                    </Typography>
                                                    <Typography variant="caption" color="textSecondary">
                                                        {format(new Date(response.created_at), 'dd/MM/yyyy HH:mm')}
                                                    </Typography>
                                                </Box>
                                                <Typography variant="body2">{response.content}</Typography>
                                            </Paper>
                                        ))}
                                    </Box>
                                ) : (
                                    <Typography variant="body2" className="text-center py-4 text-gray-500">
                                        Aún no hay respuestas
                                    </Typography>
                                )}

                                {selectedTicket.status !== 'closed' && (
                                    <Box className="mt-4">
                                        <TextField
                                            fullWidth
                                            multiline
                                            rows={4}
                                            label="Tu Respuesta"
                                            value={ticketResponse}
                                            onChange={handleResponseChange}
                                            placeholder="Escribe tu respuesta aquí..."
                                        />
                                        <Box className="flex justify-end mt-2">
                                            <Button
                                                variant="contained"
                                                color="primary"
                                                onClick={handleSubmitResponse}
                                                disabled={!ticketResponse.trim() || ticketLoading}
                                                startIcon={<AnswerIcon />}
                                            >
                                                Responder
                                            </Button>
                                        </Box>
                                    </Box>
                                )}
                            </Box>
                        </DialogContent>
                        <DialogActions>
                            {selectedTicket.status !== 'closed' && (
                                <Button
                                    onClick={handleCloseTicket}
                                    color="primary"
                                    startIcon={<CheckIcon />}
                                >
                                    Marcar como Resuelto
                                </Button>
                            )}
                            <Button onClick={handleCloseTicketDetail} startIcon={<CloseIcon />}>
                                Cerrar
                            </Button>
                        </DialogActions>
                    </>
                )}
            </Dialog>

            {/* Snackbar para mensajes de éxito */}
            <Snackbar
                open={!!successMessage}
                autoHideDuration={4000}
                onClose={() => setSuccessMessage('')}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={() => setSuccessMessage('')} severity="success" variant="filled" sx={{ width: '100%' }}>
                    {successMessage}
                </Alert>
            </Snackbar>

            {/* Snackbar para mensajes de error */}
            <Snackbar
                open={!!error}
                autoHideDuration={6000}
                onClose={() => setError(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={() => setError(null)} severity="error" variant="filled" sx={{ width: '100%' }}>
                    {error}
                </Alert>
            </Snackbar>
        </Container>
    );
};

export default ClientSupport;